import type { Theme } from "@mui/material";
import type { SystemStyleObject } from "@mui/system";
import { tokens } from "@/tokens";

/**
 * Table styling — Apple-inspired clean design.
 * Apply via spreading onto a parent container's sx prop.
 *
 * Key decisions:
 * - Header bg is solid white (not transparent) so sticky headers don't overlap body text.
 * - Header has a subtle bottom border that persists through scroll.
 * - Body cells inherit background from the row so hover works.
 * - Row hover is visible (#F9FAFB).
 */
export const tableSx: SystemStyleObject<Theme> = {
  WebkitFontSmoothing: "antialiased",
  MozOsxFontSmoothing: "grayscale",
  "& .MuiTableHead-root .MuiTableCell-root": {
    backgroundColor: `${tokens.bg.hover} !important`,
    color: `${tokens.text.secondary} !important`,
    fontSize: `${tokens.fontSize["2xs"]} !important`,
    fontWeight: `${tokens.fontWeight.normal} !important`,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    lineHeight: "14px !important",
    padding: "7px 14px !important",
    borderBottom: `1px solid ${tokens.border.default} !important`,
    borderTop: "none !important",
    borderLeft: "none !important",
    borderRight: "none !important",
    whiteSpace: "nowrap",
  },
  "& .MuiTableHead-root .MuiTableRow-root": {
    borderBottom: `1px solid ${tokens.border.default}`,
  },
  "& .MuiTableBody-root .MuiTableCell-root": {
    backgroundColor: "inherit",
    color: tokens.text.primary,
    fontSize: tokens.fontSize.sm,
    fontWeight: tokens.fontWeight.normal,
    lineHeight: "16px",
    padding: "6px 14px",
    borderTop: "none",
    borderRight: "none",
    borderBottom: `1px solid ${tokens.border.light}`,
    borderLeft: "none",
    verticalAlign: "middle",
  },
  "& .MuiTableBody-root .MuiTableRow-root": {
    backgroundColor: tokens.bg.surface,
    border: "none",
    transition: "background-color 150ms ease",
    "&:nth-of-type(even)": { backgroundColor: tokens.bg.surface },
    "&:hover": {
      backgroundColor: tokens.bg.hover,
    },
    "&:hover .MuiTableCell-root": {
      backgroundColor: "inherit",
    },
    "&:last-child .MuiTableCell-root": {
      borderBottom: `1px solid ${tokens.border.light}`,
    },
  },
  "& .MuiTableHead-root": { border: "none" },
  "& .MuiTableBody-root": { border: "none" },
};

/** Compact variant — same look with tighter padding for dense tables. */
export const tableCompactSx: SystemStyleObject<Theme> = {
  WebkitFontSmoothing: "antialiased",
  MozOsxFontSmoothing: "grayscale",
  "& .MuiTableHead-root .MuiTableCell-root": {
    backgroundColor: `${tokens.bg.hover} !important`,
    color: `${tokens.text.secondary} !important`,
    fontSize: `${tokens.fontSize["2xs"]} !important`,
    fontWeight: `${tokens.fontWeight.normal} !important`,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    lineHeight: "14px !important",
    padding: "8px 14px !important",
    borderBottom: `1px solid ${tokens.border.default} !important`,
    borderTop: "none !important",
    borderLeft: "none !important",
    borderRight: "none !important",
    whiteSpace: "nowrap",
  },
  "& .MuiTableHead-root .MuiTableRow-root": {
    borderBottom: `1px solid ${tokens.border.default}`,
  },
  "& .MuiTableBody-root .MuiTableCell-root": {
    backgroundColor: "inherit",
    color: tokens.text.body,
    fontSize: tokens.fontSize.sm2,
    fontWeight: tokens.fontWeight.book,
    lineHeight: "18px",
    padding: "13px 14px",
    borderTop: "none",
    borderRight: "none",
    borderBottom: `1px solid ${tokens.border.light}`,
    borderLeft: "none",
    verticalAlign: "middle",
  },
  "& .MuiTableBody-root .MuiTableRow-root": {
    backgroundColor: tokens.bg.surface,
    border: "none",
    transition: "background-color 150ms ease",
    "&:nth-of-type(even)": { backgroundColor: tokens.bg.surface },
    "&:hover": {
      backgroundColor: tokens.bg.hover,
    },
    "&:hover .MuiTableCell-root": {
      backgroundColor: "inherit",
    },
    "&:last-child .MuiTableCell-root": {
      borderBottom: `1px solid ${tokens.border.light}`,
    },
  },
  "& .MuiTableHead-root": { border: "none" },
  "& .MuiTableBody-root": { border: "none" },
};

export const v2TableSx = tableSx;
export const v2TableCompactSx = tableCompactSx;
/** @deprecated Use tableSx instead */
export const productHubTableSx = tableSx;

/**
 * Table container styling — rounded card with subtle border.
 */
export const tableContainerSx: SystemStyleObject<Theme> = {
  borderRadius: tokens.radius.lg,
  border: `1px solid ${tokens.border.container}`,
  overflow: "hidden",
  bgcolor: tokens.bg.surface,
  boxShadow: tokens.shadow.sm,
};

/**
 * Pagination footer styling.
 */
export const paginationSx: SystemStyleObject<Theme> = {
  flexShrink: 0,
  borderTop: `1px solid ${tokens.border.light}`,
  bgcolor: tokens.bg.surface,
  px: 2,
  py: 1,
  "& > .MuiStack-root": {
    padding: "4px 8px !important",
  },
  "& .MuiSelect-select": {
    py: "4px !important",
  },
  "& .MuiInputBase-root": {
    height: "28px !important",
  },
};

export const v2TableContainerSx = tableContainerSx;
export const v2PaginationSx = paginationSx;
/** @deprecated Use paginationSx instead */
export const productHubPaginationSx = paginationSx;
